import { useEffect, useRef, useState } from "react";

interface LazyBackgroundVideoProps {
  src: string;
  poster: string;
  posterSrcSet?: string;
  posterSizes?: string;
  className?: string;
  rootMargin?: string;
}

/**
 * Background video that only starts downloading when it gets close to the
 * viewport. Until then, only the lightweight poster image is shown, so the
 * initial page load stays fast.
 */
export default function LazyBackgroundVideo({
  src,
  poster,
  posterSrcSet,
  posterSizes = "100vw",
  className = "",
  rootMargin = "250px",
}: LazyBackgroundVideoProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);

  return (
    <div ref={containerRef} className="absolute inset-0">
      <img
        src={poster}
        srcSet={posterSrcSet}
        sizes={posterSizes}
        alt=""
        aria-hidden="true"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
          isLoaded ? "opacity-0" : "opacity-100"
        }`}
        decoding="async"
      />
      {shouldLoad && (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          poster={poster}
          onLoadedData={() => setIsLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            isLoaded ? "opacity-100" : "opacity-0"
          } ${className}`}
        >
          <source src={src} type="video/mp4" />
        </video>
      )}
    </div>
  );
}